import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

type UserRow = {
  id: string;
  email: string;
  name: string | null;
  created_at: string;
  last_login_at: string | null;
  is_admin: number;
  banned_at: string | null;
  ban_reason: string | null;
};

function timeAgo(iso: string | null) {
  if (!iso) return 'Never';
  const d = new Date(iso.includes('T') ? iso + 'Z' : iso.replace(' ', 'T') + 'Z');
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

export default function Admin() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    api
      .adminUsers()
      .then((r) => setUsers(r.users || []))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const ban = async (id: string) => {
    const reason = window.prompt('Ban reason (optional):') || undefined;
    await api.banUser(id, reason);
    load();
  };

  const unban = async (id: string) => {
    await api.unbanUser(id);
    load();
  };

  const del = async (id: string, email: string) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    await api.deleteUser(id);
    load();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Admin</h1>
        <p className="text-muted-foreground mt-1">Manage user accounts</p>
      </div>

      {error && (
        <p className="text-sm text-destructive bg-destructive/10 rounded p-2">{error}</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Users {loading ? '' : `(${users.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-4">Email</th>
                <th className="pb-2 pr-4">Created</th>
                <th className="pb-2 pr-4">Last login</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border/40">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{u.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {u.name || '—'}
                      {u.is_admin ? ' · admin' : ''}
                    </div>
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {new Date(
                      u.created_at.includes('T')
                        ? u.created_at
                        : u.created_at.replace(' ', 'T') + 'Z'
                    ).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4">
                    <div>{timeAgo(u.last_login_at)}</div>
                    {u.last_login_at && (
                      <div className="text-xs text-muted-foreground">
                        {new Date(
                          u.last_login_at.includes('T')
                            ? u.last_login_at
                            : u.last_login_at.replace(' ', 'T') + 'Z'
                        ).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    {u.banned_at ? (
                      <Badge variant="destructive">Banned</Badge>
                    ) : (
                      <Badge variant="success">Active</Badge>
                    )}
                    {u.ban_reason && (
                      <div className="text-xs text-muted-foreground mt-1">{u.ban_reason}</div>
                    )}
                  </td>
                  <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                    {u.banned_at ? (
                      <Button size="sm" variant="outline" onClick={() => unban(u.id)}>
                        Unban
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => ban(u.id)}>
                        Ban
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => del(u.id, u.email)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && users.length === 0 && (
            <p className="text-muted-foreground py-4">No users found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
